import {
  FormControl,
  FormErrorMessage,
  HStack,
  Stack,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { Text } from '@chakra-ui/layout';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Heading, Table } from '~/components';
import { SelectSymbol } from '~/pages/Portal/Settings/AutomaticAnalysis/SelectSymbol';
import { InputFloat } from '~/components/InputFloat';
import {
  getParamsExtentionAnalysis,
  patchParamsExtentionAnalysis,
} from '~/services/AutomaticAnalysis';
import { queryClient } from '~/utils/react-query';

export interface ParamsAnalysisExtension {
  cobertura_estoque: number;
  cobertura_estoque_operador: number;
  indice_falha_equipamentos: number;
  indice_falha_equipamentos_operador: number;
  indice_falha_geral: number;
  indice_falha_geral_operador: number;
  qtd_chamados: number;
  qtd_chamados_operador: number;
  tempo_vida_util: number;
  tempo_vida_util_operador: number;
}

export const AutomaticAnalysis: React.VFC = () => {
  const toast = useToast();
  const { setError, clearErrors, formState, control } = useForm();
  const { errors } = formState;
  const [automaticAnalysis, setAutomaticAnalysis] =
    useState<ParamsAnalysisExtension>({} as ParamsAnalysisExtension);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getParamsExtentionAnalysis().then(response => {
      setAutomaticAnalysis({
        cobertura_estoque: response.cobertura_estoque,
        cobertura_estoque_operador: response.cobertura_estoque_operador,
        indice_falha_equipamentos: response.indice_falha_equipamentos,
        indice_falha_equipamentos_operador:
          response.indice_falha_equipamentos_operador,
        indice_falha_geral: response.indice_falha_geral,
        indice_falha_geral_operador: response.indice_falha_geral_operador,
        qtd_chamados: response.qtd_chamados,
        qtd_chamados_operador: response.qtd_chamados_operador,
        tempo_vida_util: response.tempo_vida_util,
        tempo_vida_util_operador: response.tempo_vida_util_operador,
      });
      setIsLoading(false);
    });
  }, []);

  const onChangeValue = (
    value: number,
    label: keyof ParamsAnalysisExtension,
  ) => {
    clearErrors(label);
    const obj = {
      ...automaticAnalysis,
      [label]: value,
    };
    setAutomaticAnalysis(obj);
    if (value > 100 && label !== 'tempo_vida_util') {
      setError(label, {
        type: 'max',
        message: 'O valor deve ser menor ou igual a 100%.',
      });
    }
    if (value > 99.9 && label === 'tempo_vida_util') {
      setError(label, {
        type: 'max',
        message: 'O valor deve ser menor que 100 anos.',
      });
    }
  };

  const updateParams = (obj: Partial<ParamsAnalysisExtension>) => {
    const { _formState } = control;
    if (!_formState.isValid) return;

    patchParamsExtentionAnalysis(obj)
      .then(() => {
        toast({
          title: 'Configuração salva',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.message[0],
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        queryClient.invalidateQueries('automatic-analysis');
      });
  };

  return (
    <Stack gridGap="4" px="8" py="4">
      <Heading>Análise Automática</Heading>
      <Stack pt={3} gridGap="3" w="80%">
        <Text fontSize="md" fontWeight="bold">
          Configuração de limitantes
        </Text>
        <Table type="secondary">
          <Thead>
            <Tr>
              <Th>Características</Th>
              <Th />
              <Th w="200px">Limitante</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>
                <Stack>
                  <Text fontWeight="bold" fontSize="15px">
                    Índice de falha de equipamentos alugados (%)
                  </Text>
                  <Text
                    marginTop="0px !important"
                    fontSize="sm"
                    color="blackAlpha.500"
                  >
                    Refere-se ao índice de falha dos equipamentos alugados,
                    considerando apenas os produtos cadastrados no portal de
                    locações.
                  </Text>
                </Stack>
              </Td>
              <Td>
                <SelectSymbol
                  value={automaticAnalysis.indice_falha_equipamentos_operador}
                  onChangeValue={(e: number) => {
                    onChangeValue(e, 'indice_falha_equipamentos_operador');
                    updateParams({ indice_falha_equipamentos_operador: e });
                  }}
                />
              </Td>
              <Td>
                <HStack>
                  <FormControl isInvalid={errors.indice_falha_equipamentos}>
                    <HStack>
                      <InputFloat
                        onBlur={() => {
                          updateParams({
                            indice_falha_equipamentos:
                              automaticAnalysis.indice_falha_equipamentos,
                          });
                        }}
                        name="indice_falha"
                        size="sm"
                        onChangeValue={e => {
                          onChangeValue(e, 'indice_falha_equipamentos');
                        }}
                        value={automaticAnalysis.indice_falha_equipamentos}
                      />
                      <Text fontWeight="bold" w="50px">
                        (%)
                      </Text>
                    </HStack>
                    {!!errors.indice_falha_equipamentos && (
                      <FormErrorMessage>
                        {errors.indice_falha_equipamentos.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <Stack>
                  <Text fontWeight="bold" fontSize="15px">
                    Cobertura estoque (%)
                  </Text>
                  <Text
                    marginTop="0px !important"
                    fontSize="sm"
                    color="blackAlpha.500"
                  >
                    A cobertura de estoque representa a relação entre a
                    quantidade de produtos em estoque e a quantidade de produtos
                    em uso no parque.
                  </Text>
                </Stack>
              </Td>
              <Td>
                <SelectSymbol
                  value={automaticAnalysis.cobertura_estoque_operador}
                  onChangeValue={e => {
                    onChangeValue(e, 'cobertura_estoque_operador');
                    updateParams({ cobertura_estoque_operador: e });
                  }}
                />
              </Td>
              <Td>
                <HStack>
                  <FormControl isInvalid={errors.cobertura_estoque}>
                    <HStack>
                      <InputFloat
                        onBlur={() => {
                          updateParams({
                            cobertura_estoque:
                              automaticAnalysis.cobertura_estoque,
                          });
                        }}
                        name="cobertura_estoque"
                        size="sm"
                        onChangeValue={e => {
                          onChangeValue(e, 'cobertura_estoque');
                        }}
                        value={automaticAnalysis.cobertura_estoque}
                        decimalPlaces={4}
                      />
                      <Text fontWeight="bold" w="50px">
                        (%)
                      </Text>
                    </HStack>
                    {!!errors.cobertura_estoque && (
                      <FormErrorMessage>
                        {errors.cobertura_estoque.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <Stack>
                  <Text fontWeight="bold" fontSize="15px">
                    Tempo de vida útil (em anos)
                  </Text>
                  <Text
                    marginTop="0px !important"
                    fontSize="sm"
                    color="blackAlpha.500"
                  >
                    Corresponde à diferença entre a data atual e a data de
                    ativação do produto.
                  </Text>
                </Stack>
              </Td>
              <Td>
                <SelectSymbol
                  value={automaticAnalysis.tempo_vida_util_operador}
                  onChangeValue={e => {
                    onChangeValue(e, 'tempo_vida_util_operador');
                    updateParams({ tempo_vida_util_operador: e });
                  }}
                />
              </Td>
              <Td>
                <HStack>
                  <FormControl isInvalid={errors.tempo_vida_util}>
                    <HStack>
                      <InputFloat
                        onBlur={() => {
                          updateParams({
                            tempo_vida_util: automaticAnalysis.tempo_vida_util,
                          });
                        }}
                        name="tempo_vida_util"
                        size="sm"
                        onChangeValue={e => {
                          onChangeValue(e, 'tempo_vida_util');
                        }}
                        value={automaticAnalysis.tempo_vida_util}
                        decimalPlaces={1}
                      />
                      <Text fontWeight="bold" w="50px">
                        ano(s)
                      </Text>
                    </HStack>
                    {!!errors.tempo_vida_util && (
                      <FormErrorMessage>
                        {errors.tempo_vida_util.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <Stack>
                  <Text fontWeight="bold" fontSize="15px">
                    Índice de falha geral (%)
                  </Text>
                  <Text
                    marginTop="0px !important"
                    fontSize="sm"
                    color="blackAlpha.500"
                  >
                    Refere-se ao índice de falha dos equipamentos em uso no
                    parque.
                  </Text>
                </Stack>
              </Td>
              <Td>
                <SelectSymbol
                  value={automaticAnalysis.indice_falha_geral_operador}
                  onChangeValue={e => {
                    onChangeValue(e, 'indice_falha_geral_operador');
                    updateParams({ indice_falha_geral_operador: e });
                  }}
                />
              </Td>
              <Td>
                <HStack>
                  <FormControl isInvalid={errors.indice_falha_geral}>
                    <HStack>
                      <InputFloat
                        onBlur={() => {
                          updateParams({
                            indice_falha_geral:
                              automaticAnalysis.indice_falha_geral,
                          });
                        }}
                        name="indice_falha_geral"
                        size="sm"
                        onChangeValue={e => {
                          onChangeValue(e, 'indice_falha_geral');
                        }}
                        value={automaticAnalysis.indice_falha_geral}
                      />
                      <Text fontWeight="bold" w="50px">
                        (%)
                      </Text>
                    </HStack>
                    {!!errors.indice_falha_geral && (
                      <FormErrorMessage>
                        {errors.indice_falha_geral.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <Stack>
                  <Text fontWeight="bold" fontSize="15px">
                    Quantidade de chamados (%)
                  </Text>
                  <Text
                    marginTop="0px !important"
                    fontSize="sm"
                    color="blackAlpha.500"
                  >
                    Percentual de equipamentos do contrato que receberam
                    chamados.
                  </Text>
                </Stack>
              </Td>
              <Td>
                <SelectSymbol
                  value={automaticAnalysis.qtd_chamados_operador}
                  onChangeValue={e => {
                    onChangeValue(e, 'qtd_chamados_operador');
                    updateParams({ qtd_chamados_operador: e });
                  }}
                />
              </Td>
              <Td>
                <HStack>
                  <FormControl isInvalid={errors.qtd_chamados}>
                    <HStack>
                      <InputFloat
                        onBlur={() => {
                          updateParams({
                            qtd_chamados: automaticAnalysis.qtd_chamados,
                          });
                        }}
                        name="qtd_chamados"
                        size="sm"
                        onChangeValue={e => {
                          onChangeValue(e, 'qtd_chamados');
                        }}
                        value={automaticAnalysis.qtd_chamados}
                      />
                      <Text fontWeight="bold" w="50px">
                        (%)
                      </Text>
                    </HStack>
                    {!!errors.qtd_chamados && (
                      <FormErrorMessage>
                        {errors.qtd_chamados.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Stack>
    </Stack>
  );
};
